import React, { useMemo } from 'react';
import { formatUTC } from '../utils/commonUtils';
import './TrendRangeSlider.scss';

interface TrendRangeSliderProps {
    /** Full window lower bound (e.g. now-1y). */
    windowStart: Date;
    /** Full window upper bound (typically now / latest snapshot). */
    windowEnd: Date;
    /** Currently-selected sub-range start/end (within the window). */
    selectedStart: Date;
    selectedEnd: Date;
    /** Emits the new sub-range when either thumb moves. */
    onChange: (start: Date, end: Date) => void;
    /** Human label for the full window (e.g. "Last 1 Year"). */
    windowLabel?: string;
}

// Discrete steps across the window. Snapshots are ~15-min, but the backend aligns the
// requested range to the actual snapshots, so a fixed step count keeps the control smooth
// regardless of window size.
const STEPS = 240;

/**
 * Dual-thumb range slider bounded by the selected timeWindow ([windowStart, windowEnd]).
 * Dragging either thumb selects a sub-range; the parent refetches trend snapshots for it.
 */
const TrendRangeSlider: React.FC<TrendRangeSliderProps> = ({
    windowStart,
    windowEnd,
    selectedStart,
    selectedEnd,
    onChange,
    windowLabel,
}) => {
    const windowStartMs = windowStart.getTime();
    const windowEndMs = windowEnd.getTime();
    const span = Math.max(1, windowEndMs - windowStartMs);

    const toStep = (d: Date) => {
        const clamped = Math.min(windowEndMs, Math.max(windowStartMs, d.getTime()));
        return Math.round(((clamped - windowStartMs) / span) * STEPS);
    };
    const toDate = (step: number) => new Date(windowStartMs + (step / STEPS) * span);

    const startStep = useMemo(() => toStep(selectedStart), [selectedStart, windowStartMs, windowEndMs]);
    const endStep = useMemo(() => toStep(selectedEnd), [selectedEnd, windowStartMs, windowEndMs]);

    const handleStartChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        const next = Math.min(Number(e.target.value), endStep - 1);
        onChange(toDate(Math.max(0, next)), selectedEnd);
    };
    const handleEndChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        const next = Math.max(Number(e.target.value), startStep + 1);
        onChange(selectedStart, toDate(Math.min(STEPS, next)));
    };

    const leftPct = (startStep / STEPS) * 100;
    const rightPct = (endStep / STEPS) * 100;

    return (
        <div className="trend-range-slider">
            <div className="trend-range-slider__labels">
                <span className="trend-range-slider__window">{windowLabel || 'Selected window'}</span>
                <span className="trend-range-slider__range">
                    {formatUTC(selectedStart)} &rarr; {formatUTC(selectedEnd)} UTC
                </span>
            </div>
            <div className="trend-range-slider__track-wrap">
                <div className="trend-range-slider__track" />
                <div
                    className="trend-range-slider__fill"
                    style={{ left: `${leftPct}%`, right: `${100 - rightPct}%` }}
                />
                <input
                    type="range"
                    min={0}
                    max={STEPS}
                    value={startStep}
                    onChange={handleStartChange}
                    aria-label="Trend range start"
                    className="trend-range-slider__thumb trend-range-slider__thumb--start"
                />
                <input
                    type="range"
                    min={0}
                    max={STEPS}
                    value={endStep}
                    onChange={handleEndChange}
                    aria-label="Trend range end"
                    className="trend-range-slider__thumb trend-range-slider__thumb--end"
                />
            </div>
            <div className="trend-range-slider__bounds">
                <span>{formatUTC(windowStart)}</span>
                <span>{formatUTC(windowEnd)}</span>
            </div>
        </div>
    );
};

export default TrendRangeSlider;
